import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, timeAgo } from "./shared";
import { TypeDeleteDialog } from "@/components/TypeDeleteDialog";
import { Archive, ArchiveRestore, Trash2, Loader2, Search, ShieldAlert, Download, Tag, UserPlus } from "lucide-react";

const TABS = [["active", "Active"], ["archived", "Archived"], ["all", "All"]];

// Unified, registry-driven Archive → Restore → Delete surface. Every module
// registered in the backend MODULE_REGISTRY automatically appears here.
export function RecordsCenter() {
  const [registry, setRegistry] = useState(null);
  const [module, setModule] = useState(null);
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [delDialog, setDelDialog] = useState(null); // { ids: [] } | { bulk: true }
  const [panel, setPanel] = useState(null); // 'tag' | 'assign'
  const [tagText, setTagText] = useState("");
  const [assignText, setAssignText] = useState("");

  useEffect(() => {
    api.get("/lifecycle/registry").then((r) => {
      setRegistry(r.data);
      setModule(r.data[0]?.module || null);
    }).catch(() => setRegistry([]));
  }, []);

  const modCfg = useMemo(() => (registry || []).find((m) => m.module === module), [registry, module]);

  const load = useCallback(() => {
    if (!module) return;
    setData(null); setSel(new Set());
    api.get(`/lifecycle/${module}`, { params: { status: tab, q: q || undefined } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} }));
  }, [module, tab, q]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const allowDelete = !!modCfg?.allow_permanent_delete;

  const act = async (id, action, reason = "") => {
    setBusy(true);
    try {
      if (action === "archive") await api.post(`/lifecycle/${module}/${id}/archive`, { reason });
      else if (action === "restore") await api.post(`/lifecycle/${module}/${id}/restore`);
      toast.success(action === "archive" ? "Record archived." : "Record restored.");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    const ids = delDialog?.ids || [];
    try {
      if (ids.length === 1) await api.post(`/lifecycle/${module}/${ids[0]}/delete`, { confirm: "DELETE" });
      else await api.post(`/lifecycle/${module}/bulk`, { action: "delete", ids, confirm: "DELETE" });
      toast.success(`Permanently deleted ${ids.length} record${ids.length === 1 ? "" : "s"}.`);
      setDelDialog(null); load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const bulk = async (action) => {
    const ids = [...sel];
    if (!ids.length) return;
    if (action === "delete") { setDelDialog({ ids }); return; }
    setBusy(true);
    try {
      await api.post(`/lifecycle/${module}/bulk`, { action, ids });
      toast.success(`${action === "archive" ? "Archived" : "Restored"} ${ids.length} record${ids.length === 1 ? "" : "s"}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel((s) => s.size === items.length ? new Set() : new Set(items.map((i) => i.id)));
  const selectVisible = () => setSel(new Set(items.map((i) => i.id)));
  const clearSel = () => setSel(new Set());

  const exportSel = async () => {
    const ids = [...sel];
    setBusy(true);
    try {
      const res = await api.post(`/lifecycle/${module}/export`, { ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${module}-export.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${ids.length} record${ids.length === 1 ? "" : "s"} to CSV.`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const applyTags = async () => {
    const tags = tagText.split(",").map((t) => t.trim()).filter(Boolean);
    if (!tags.length) { toast.error("Enter at least one tag"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/lifecycle/${module}/bulk-tag`, { ids: [...sel], tags, mode: "add" });
      toast.success(`Tagged ${data.processed} record${data.processed === 1 ? "" : "s"}.`);
      setPanel(null); setTagText(""); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const applyAssign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lifecycle/${module}/bulk-assign`, { ids: [...sel], assignee: assignText.trim() || null });
      toast.success(`Assigned ${data.processed} record${data.processed === 1 ? "" : "s"}${assignText.trim() ? ` to ${assignText.trim()}` : " (cleared)"}.`);
      setPanel(null); setAssignText(""); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!registry) return <Loader />;

  return (
    <div>
      <Head title="Records Center" desc="Platform-wide Archive → Restore → Delete. Protects business data with a consistent lifecycle across every module." />

      {/* Module selector */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="records-module-tabs">
        {registry.map((m) => (
          <button key={m.module} onClick={() => { setModule(m.module); setTab("active"); setQ(""); }}
            data-testid={`records-module-${m.module}`}
            className={`px-3.5 py-2 text-sm border transition-colors ${module === m.module ? "bg-gold text-navy border-gold font-semibold" : "bg-navy-800 border-white/10 text-gray-400 hover:text-white hover:border-gold/40"}`}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Status tabs + search */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex gap-1 bg-navy-800 border border-white/10 p-1" data-testid="records-status-tabs">
          {TABS.map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`records-tab-${k}`}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${tab === k ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>
              {lbl}{data?.counts?.[k] != null ? ` (${data.counts[k]})` : ""}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${modCfg?.label?.toLowerCase() || "records"}…`}
            data-testid="records-search" className="w-full bg-navy-800 border border-white/10 pl-9 pr-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
        </div>
      </div>

      {!allowDelete && (
        <div className="flex items-center gap-2 text-xs text-gold/90 bg-gold/10 border border-gold/30 px-3 py-2 mb-4" data-testid="records-archive-only">
          <ShieldAlert size={14} /> {modCfg?.label} are archive-only — permanent deletion is disabled to protect financial & compliance records.
        </div>
      )}

      {/* Bulk action bar */}
      {sel.size > 0 && (
        <div className="bg-navy border border-gold/30 px-4 py-2.5 mb-4 space-y-3" data-testid="records-bulk-bar">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-white font-medium" data-testid="records-selected-count">{sel.size} selected</span>
            <button onClick={selectVisible} data-testid="bulk-select-visible" className="text-xs text-gray-400 hover:text-gold">Select visible</button>
            <button onClick={clearSel} data-testid="bulk-clear" className="text-xs text-gray-400 hover:text-white">Clear</button>
            <div className="flex-1" />
            {tab !== "archived" && <BulkBtn icon={Archive} label="Archive" onClick={() => bulk("archive")} testid="bulk-archive" busy={busy} />}
            {tab !== "active" && <BulkBtn icon={ArchiveRestore} label="Restore" onClick={() => bulk("restore")} testid="bulk-restore" busy={busy} />}
            {allowDelete && tab !== "active" && <BulkBtn icon={Trash2} label="Delete" onClick={() => bulk("delete")} testid="bulk-delete" danger busy={busy} />}
            <BulkBtn icon={Download} label="Export" onClick={exportSel} testid="bulk-export" busy={busy} />
            <BulkBtn icon={Tag} label="Tag" onClick={() => setPanel(panel === "tag" ? null : "tag")} testid="bulk-tag" busy={busy} />
            <BulkBtn icon={UserPlus} label="Assign" onClick={() => setPanel(panel === "assign" ? null : "assign")} testid="bulk-assign" busy={busy} />
          </div>
          {panel === "tag" && (
            <div className="flex items-center gap-2 border-t border-white/10 pt-3" data-testid="bulk-tag-panel">
              <input value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="Tags (comma separated)…" data-testid="bulk-tag-input"
                className="flex-1 bg-navy-800 border border-white/15 px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" onKeyDown={(e) => e.key === "Enter" && applyTags()} />
              <button onClick={applyTags} disabled={busy} data-testid="bulk-tag-apply" className="bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light disabled:opacity-60">Apply tags</button>
            </div>
          )}
          {panel === "assign" && (
            <div className="flex items-center gap-2 border-t border-white/10 pt-3" data-testid="bulk-assign-panel">
              <input value={assignText} onChange={(e) => setAssignText(e.target.value)} placeholder="Assignee name (blank to clear)…" data-testid="bulk-assign-input"
                className="flex-1 bg-navy-800 border border-white/15 px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" onKeyDown={(e) => e.key === "Enter" && applyAssign()} />
              <button onClick={applyAssign} disabled={busy} data-testid="bulk-assign-apply" className="bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light disabled:opacity-60">Assign</button>
            </div>
          )}
        </div>
      )}

      {!data ? <Loader /> : items.length === 0 ? (
        <div className="bg-navy-800 border border-white/5 p-12 text-center text-gray-500 text-sm" data-testid="records-empty">
          No {tab === "all" ? "" : tab} {modCfg?.label?.toLowerCase()} found.
        </div>
      ) : (
        <div className="bg-navy-800 border border-white/5 overflow-x-auto" data-testid="records-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b border-white/10">
              <th className="p-3 w-10"><input type="checkbox" className="accent-gold" checked={sel.size === items.length && items.length > 0} onChange={toggleAll} data-testid="records-select-all" /></th>
              <th className="p-3 font-medium">{modCfg?.singular || "Record"}</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Archive info</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr></thead>
            <tbody>
              {items.map((it) => {
                const archived = it.lifecycle_status === "archived";
                return (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`record-row-${it.id}`}>
                    <td className="p-3"><input type="checkbox" className="accent-gold" checked={sel.has(it.id)} onChange={() => toggleSel(it.id)} data-testid={`record-select-${it.id}`} /></td>
                    <td className="p-3 text-white max-w-[280px]">
                      <div className="truncate">{it.name}</div>
                      {(it.tags?.length > 0 || it.assigned_to) && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          {it.assigned_to && <span className="text-[10px] text-blue-300 bg-blue-500/10 border border-blue-500/30 px-1.5 py-0.5 rounded-full">@{it.assigned_to}</span>}
                          {(it.tags || []).map((tg) => <span key={tg} className="text-[10px] text-gold bg-gold/10 border border-gold/30 px-1.5 py-0.5 rounded-full">{tg}</span>)}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border rounded-full ${archived ? "bg-white/10 text-gray-400 border-white/20" : "bg-green-500/15 text-green-300 border-green-500/30"}`} data-testid={`record-status-${it.id}`}>
                        {archived ? "Archived" : "Active"}
                      </span>
                    </td>
                    <td className="p-3 text-gray-500 text-xs">
                      {archived ? (
                        <div>
                          <div>{it.archived_by || "—"} · {it.archived_at ? timeAgo(it.archived_at) : ""}</div>
                          {it.archive_reason ? <div className="text-gray-600 italic truncate max-w-[220px]">“{it.archive_reason}”</div> : null}
                        </div>
                      ) : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        {archived ? (
                          <>
                            <RowBtn icon={ArchiveRestore} label="Restore" onClick={() => act(it.id, "restore")} testid={`record-restore-${it.id}`} disabled={busy} />
                            {allowDelete && <RowBtn icon={Trash2} label="Delete" onClick={() => setDelDialog({ ids: [it.id] })} testid={`record-delete-${it.id}`} danger disabled={busy} />}
                          </>
                        ) : (
                          <RowBtn icon={Archive} label="Archive" onClick={() => act(it.id, "archive")} testid={`record-archive-${it.id}`} disabled={busy} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <TypeDeleteDialog open={!!delDialog} count={delDialog?.ids?.length || 0} label={(modCfg?.singular || "record").toLowerCase()}
        onConfirm={doDelete} onCancel={() => setDelDialog(null)} />
    </div>
  );
}

const RowBtn = ({ icon: Icon, label, onClick, testid, danger, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid} title={label}
    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border transition-colors disabled:opacity-40 ${danger ? "border-red-500/30 text-red-300 hover:bg-red-500/10" : "border-white/10 text-gray-200 hover:border-gold/50 hover:text-white hover:bg-white/5"}`}>
    <Icon size={13} /> {label}
  </button>
);

const BulkBtn = ({ icon: Icon, label, onClick, testid, danger, busy }) => (
  <button onClick={onClick} disabled={busy} data-testid={testid}
    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors disabled:opacity-50 ${danger ? "border-red-500/40 text-red-300 hover:bg-red-500/10" : "border-gold/40 text-gold hover:bg-gold/10"}`}>
    {busy ? <Loader2 className="animate-spin" size={13} /> : <Icon size={13} />} {label}
  </button>
);
